'use client'

import { cn } from '@/lib/utils'
import { EmblaCarouselType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'
import debounce from 'lodash.debounce'
import { useCallback, useEffect, useMemo, useRef } from 'react'

const CIRCLE_DEGREES = 360
const WHEEL_ITEM_SIZE = 32
const WHEEL_ITEM_COUNT = 18
const WHEEL_ITEMS_IN_VIEW = 4

export const WHEEL_ITEM_RADIUS = CIRCLE_DEGREES / WHEEL_ITEM_COUNT
export const IN_VIEW_DEGREES = WHEEL_ITEM_RADIUS * WHEEL_ITEMS_IN_VIEW
export const WHEEL_RADIUS = Math.round(
  WHEEL_ITEM_SIZE / 2 / Math.tan(Math.PI / WHEEL_ITEM_COUNT)
)

const isInView = (wheelLocation: number, slidePosition: number): boolean =>
  Math.abs(wheelLocation - slidePosition) < IN_VIEW_DEGREES

const setSlideStyles = (
  emblaApi: EmblaCarouselType,
  index: number,
  loop: boolean,
  slideCount: number,
  totalRadius: number
): void => {
  const slideNode = emblaApi.slideNodes()[index]
  const wheelLocation = emblaApi.scrollProgress() * totalRadius
  const positionDefault = emblaApi.scrollSnapList()[index] * totalRadius
  const positionLoopStart = positionDefault + totalRadius
  const positionLoopEnd = positionDefault - totalRadius
  const currentIndex = emblaApi?.selectedScrollSnap()
  const isSelected = index === currentIndex

  let inView = false
  let angle = index * -WHEEL_ITEM_RADIUS

  if (isInView(wheelLocation, positionDefault)) {
    inView = true
  }

  if (loop && isInView(wheelLocation, positionLoopEnd)) {
    inView = true
    angle = -CIRCLE_DEGREES + (slideCount - index) * WHEEL_ITEM_RADIUS
  }

  if (loop && isInView(wheelLocation, positionLoopStart)) {
    inView = true
    angle = -(totalRadius % CIRCLE_DEGREES) - index * WHEEL_ITEM_RADIUS
  }

  if (inView) {
    slideNode.style.opacity = '1'
    slideNode.style.transform = `translateY(-${
      index * 100
    }%) rotateX(${angle}deg) translateZ(${WHEEL_RADIUS}px)`
    slideNode.style.transition = 'transform 0.2s ease-out'
  } else {
    slideNode.style.opacity = '0'
    slideNode.style.transform = 'none'
  }

  if (isSelected) {
    slideNode.classList.add('text-primary', 'scale-105', 'font-medium')
  } else {
    slideNode.classList.remove('text-primary', 'scale-105', 'font-medium')
  }
}

export const setContainerStyles = (
  emblaApi: EmblaCarouselType,
  wheelRotation: number
): void => {
  emblaApi.containerNode().style.transform = `translateZ(${WHEEL_RADIUS}px) rotateX(${wheelRotation}deg)`
}

type PropType = {
  title?: string
  options: {
    label: string
    value: number
  }[]
  loop?: boolean
  perspective?: 'left' | 'right'
  selected?: number
  onChange?: (value: number, index: number) => void
  className?: string
}

export const SelectPicker: React.FC<PropType> = ({
  loop = false,
  perspective = 'left',
  selected,
  onChange,
  options,
  title,
  className,
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop,
    axis: 'y',
    dragFree: true,
    containScroll: false,
    watchSlides: false,
  })
  const slideCount = options.length

  const rootNodeRef = useRef<HTMLDivElement>(null)
  const totalRadius = slideCount * WHEEL_ITEM_RADIUS
  const rotationOffset = loop ? 0 : WHEEL_ITEM_RADIUS
  const lastIndexRef = useRef<number | null>(null)

  const inactivateEmblaTransform = useCallback(
    (emblaApi: EmblaCarouselType) => {
      if (!emblaApi) return
      const { translate, slideLooper } = emblaApi.internalEngine()
      translate.clear()
      translate.toggleActive(false)
      slideLooper.loopPoints.forEach(({ translate }) => {
        translate.clear()
        translate.toggleActive(false)
      })
    },
    []
  )

  const rotateWheel = useCallback(
    (emblaApi: EmblaCarouselType) => {
      const rotation = slideCount * WHEEL_ITEM_RADIUS - rotationOffset
      const wheelRotation = rotation * emblaApi.scrollProgress()
      setContainerStyles(emblaApi, wheelRotation)
      emblaApi.slideNodes().forEach((_, index) => {
        setSlideStyles(emblaApi, index, loop, slideCount, totalRadius)
      })
    },
    [slideCount, rotationOffset, totalRadius, loop]
  )
  const debouncedRotateWheel = useMemo(
    () => debounce(rotateWheel, 16), // ~60fps
    [rotateWheel]
  )

  useEffect(() => {
    if (!emblaApi || selected == null) return
    emblaApi.scrollTo(selected, true)
  }, [emblaApi, selected])

  const emitChange = useCallback(
    (emblaApi: EmblaCarouselType) => {
      const index = emblaApi.selectedScrollSnap()
      const value = options[index]?.value
      if (index !== lastIndexRef.current && value !== undefined) {
        lastIndexRef.current = index
        onChange?.(value, index)
      }
    },
    [onChange, options]
  )
  useEffect(() => {
    if (!emblaApi) return

    const pointerUpHandler = (emblaApi: EmblaCarouselType) => {
      const { scrollTo, target, location } = emblaApi.internalEngine()
      const diffToTarget = target.get() - location.get()
      const factor = Math.abs(diffToTarget) < WHEEL_ITEM_SIZE / 2.5 ? 10 : 0.1
      const distance = diffToTarget * factor
      scrollTo.distance(distance, true)
    }

    emblaApi.on('pointerUp', pointerUpHandler)
    emblaApi.on('scroll', debouncedRotateWheel)
    emblaApi.on('select', emitChange)
    emblaApi.on('reInit', (emblaApi) => {
      inactivateEmblaTransform(emblaApi)
      debouncedRotateWheel(emblaApi)
      debouncedRotateWheel.flush()
      emitChange(emblaApi)
    })

    inactivateEmblaTransform(emblaApi)
    debouncedRotateWheel(emblaApi)
    emitChange(emblaApi)

    return () => {
      emblaApi.off('pointerUp', pointerUpHandler)
      debouncedRotateWheel.cancel()
    }
  }, [emblaApi, debouncedRotateWheel, emitChange, inactivateEmblaTransform])

  useEffect(() => {
    if (!emblaApi) return

    emblaApi.on('scroll', rotateWheel)

    emblaApi.on('select', emitChange)

    emblaApi.on('reInit', (emblaApi) => {
      inactivateEmblaTransform(emblaApi)
      debouncedRotateWheel(emblaApi)
      debouncedRotateWheel.flush()
      emitChange(emblaApi)
    })
    inactivateEmblaTransform(emblaApi)
    rotateWheel(emblaApi)
    emitChange(emblaApi)
  }, [
    emblaApi,
    inactivateEmblaTransform,
    rotateWheel,
    emitChange,
    debouncedRotateWheel,
  ])

  useEffect(() => {
    if (!emblaApi || !rootNodeRef.current) return

    const handleWheel = throttle((...args: unknown[]) => {
      const event = args[0] as WheelEvent
      event.preventDefault()
      if (event.deltaY > 0) {
        emblaApi.scrollNext()
      } else if (event.deltaY < 0) {
        emblaApi.scrollPrev()
      }
    }, 200)
    const node = rootNodeRef.current as HTMLElement
    node.addEventListener('wheel', handleWheel, { passive: false })
    return () => {
      node.removeEventListener('wheel', handleWheel)
      if (handleWheel.cancel) {
        handleWheel.cancel()
      }
    }
  }, [emblaApi])

  return (
    <div className={cn('relative mx-auto flex h-52 w-full', className)}>
      <div className='pointer-events-none absolute left-0 right-0 top-1/2 z-10 h-px -translate-y-[calc(50%-16px)] bg-neutral-300' />
      <div className='pointer-events-none absolute left-0 right-0 top-1/2 z-10 h-px translate-y-[calc(50%-16px)] bg-neutral-300' />
      {title && (
        <p className='absolute top-1/4 font-sans text-sm leading-4.5 text-neutral-500'>
          {title}
        </p>
      )}
      <div className='flex h-full w-full items-center justify-center overflow-hidden text-sm leading-none'>
        <div
          className='flex h-full min-w-full touch-pan-y items-center overflow-hidden'
          ref={rootNodeRef}
        >
          <div
            ref={emblaRef}
            className={`h-8 w-full touch-none select-none [perspective:1000px] ${
              perspective === 'left'
                ? 'translate-x-7 [perspective-origin:calc(50%+130px)_50%]'
                : '-translate-x-7 [perspective-origin:calc(50%-130px)_50%]'
            }`}
          >
            <div className='h-full w-full will-change-transform [transform-style:preserve-3d]'>
              {options.map((option) => {
                return (
                  <div
                    key={option.value}
                    className={
                      'flex h-full w-full items-center justify-center text-center font-sans text-xs font-normal leading-4.5 text-neutral-800/50 transition-transform duration-200'
                    }
                  >
                    {option.label}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
      <span className='hidden scale-105 font-medium text-primary'></span>
    </div>
  )
}

function throttle<T extends (...args: unknown[]) => void>(
  func: T,
  wait: number
) {
  let lastTime = 0
  let timeout: ReturnType<typeof setTimeout> | null = null
  let lastArgs: unknown[]
  const throttled = function (this: unknown, ...args: unknown[]) {
    const now = Date.now()
    const remaining = wait - (now - lastTime)
    lastArgs = args
    if (remaining <= 0) {
      if (timeout) {
        clearTimeout(timeout)
        timeout = null
      }
      lastTime = now
      func.apply(this, args)
    } else if (!timeout) {
      timeout = setTimeout(() => {
        lastTime = Date.now()
        timeout = null
        func.apply(this, lastArgs)
      }, remaining)
    }
  }
  throttled.cancel = () => {
    if (timeout) {
      clearTimeout(timeout)
      timeout = null
    }
  }
  return throttled as T & { cancel: () => void }
}
